import { useState, useEffect, useRef, useCallback } from 'react';
import { SnackbarType } from '../../components/Snackbar/types';
import { SnackbarInfo, UseSnackbarProviderReturn } from './types';

/**
 * SnackbarProviderのロジックを管理するカスタムフック
 * スナックバーの表示・非表示の制御と複数スナックバーの管理を行う
 */
export const useSnackbarProvider = (): UseSnackbarProviderReturn => {
  // 複数のスナックバーを管理するための配列
  const [snackbars, setSnackbars] = useState<SnackbarInfo[]>([]);
  
  // 自動クローズ用にタイマーを保持
  const snackbarTimers = useRef<Record<string, NodeJS.Timeout>>({});
  
  // IDの一意性を確保するためのカウンター
  const idCounter = useRef<number>(0);

  // 重複防止のためのメッセージ履歴（メッセージ + タイプ + タイムスタンプ）
  const messageHistory = useRef<Map<string, number>>(new Map());
  const historyTtlMs = 5 * 60 * 1000; // 5分
  const historyMaxSize = 200;

  // 特定のスナックバーを閉じる
  const closeSnackbar = useCallback((id: string): void => {
    setSnackbars(prev => 
      prev.map(sb => 
        sb.id === id ? { ...sb, open: false } : sb
      )
    );
    
    // 閉じたスナックバーを配列から削除するタイマー
    setTimeout(() => {
      setSnackbars(prev => prev.filter(sb => sb.id !== id));
      
      // タイマーの参照も削除
      if (snackbarTimers.current[id]) {
        clearTimeout(snackbarTimers.current[id]);
        delete snackbarTimers.current[id];
      }
    }, 500); // アニメーション完了後に削除
  }, []);

  /**
   * スナックバーを表示する関数。
   * @param {string} msg - 表示するメッセージ
   * @param {SnackbarType} variant - タイプ（warning, error, successなど）
   * @param {number} duration - 自動クローズまでの時間（ミリ秒）
   */
  const showSnackbar = useCallback((msg: string, variant: SnackbarType = SnackbarType.DEFAULT, duration: number = 3000): void => {
    // 古い履歴を整理してメモリ増加を抑制
    const now = Date.now();
    messageHistory.current.forEach((timestamp, key) => {
      if (now - timestamp > historyTtlMs) {
        messageHistory.current.delete(key);
      }
    });
    if (messageHistory.current.size > historyMaxSize) {
      const entries = Array.from(messageHistory.current.entries()).sort((a, b) => a[1] - b[1]);
      const deleteCount = entries.length - historyMaxSize;
      for (let i = 0; i < deleteCount; i += 1) {
        messageHistory.current.delete(entries[i][0]);
      }
    }

    // 重複防止のためのキーを生成
    const duplicateKey = `${msg}-${variant}`;
    const lastShownTime = messageHistory.current.get(duplicateKey);

    // 同じメッセージが5秒以内に表示されていた場合は無視
    if (lastShownTime && now - lastShownTime < 5000) {
      return;
    }

    // メッセージ履歴を更新
    messageHistory.current.set(duplicateKey, now);

    // 一意のIDを生成（タイムスタンプ + カウンター）
    idCounter.current += 1;
    const id = `${Date.now()}-${idCounter.current}`;

    // 新しいスナックバーを追加
    const newSnackbar: SnackbarInfo = {
      id,
      message: msg,
      type: variant,
      open: true
    };

    // 表示中のスナックバーが5個を超えないように制限
    setSnackbars(prev => {
      const updatedSnackbars = [...prev, newSnackbar];
      if (updatedSnackbars.length > 5) {
        // 最古のスナックバーを削除
        const oldestSnackbar = updatedSnackbars[0];
        if (snackbarTimers.current[oldestSnackbar.id]) {
          clearTimeout(snackbarTimers.current[oldestSnackbar.id]);
          delete snackbarTimers.current[oldestSnackbar.id];
        }
        return updatedSnackbars.slice(1);
      }
      return updatedSnackbars;
    });

    // 自動クローズのタイマーを設定
    snackbarTimers.current[id] = setTimeout(() => {
      closeSnackbar(id);
    }, duration);
  }, [closeSnackbar]);
  
  // コンポーネントがアンマウントする際に自動クローズのタイマーをクリア
  useEffect(() => {
    return () => {
      Object.values(snackbarTimers.current).forEach(timer => {
        clearTimeout(timer);
      });
    };
  }, []);

  return {
    showSnackbar,
    snackbars,
    closeSnackbar
  };
}; 
